'use client';

/**
 * Pinned-card media bodies — video/article renderers shared by desktop's
 * pinned-card.tsx (unchanged turn-11 presentation) and mobile's
 * pinned-media-compact.tsx (design turn 14). Extracted out of
 * pinned-card.tsx (where these originated) into their own file specifically
 * so the mobile compact wrapper can reuse them without pinned-card.tsx ↔
 * pinned-card-mobile.tsx forming an import cycle.
 *
 * No sizing/layout changes here — media pins were already compact-ish per
 * the turn-14 brief, so mobile reuses these bodies as-is; only the
 * surrounding chrome (header dots, SwipeTrack) differs by platform.
 */

import { memo, useMemo } from 'react';
import { Play } from 'lucide-react';
import { getYouTubeId, youTubeThumbnailUrl } from '@/lib/youtube';
import { defaultPreview } from '@/components/assistant/content/default-preview';
import { deriveMediaKind } from '@/components/assistant/thinking/media-kind';
import { useYouTubeDuration } from '@/hooks/useYouTubeDuration';
import type { RAGReference } from '@/types/rag';
import { cn } from '@/lib/utils';
import { V3_SHADOWS } from '@/lib/design-tokens';

/** Same "strip www., keep the bare host" convention as source-badge.tsx /
 *  rag-answer-block.tsx's ArticleHero — falls back to the raw url on a
 *  malformed value rather than throwing. */
export function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function MediaBody({ refItem }: { refItem: RAGReference }) {
  // Binary video/article split on the shared classifier — everything that
  // isn't 'article' (video/meditation/talk/short/quote/other) renders as the
  // video-shaped body; only type==='Text' produces 'article'.
  return deriveMediaKind(refItem) === 'article' ? (
    <ArticleBody refItem={refItem} />
  ) : (
    <VideoBody refItem={refItem} />
  );
}

// Memoized: SwipeTrack's drag state is local to itself (see swipe-track.tsx),
// so `refItem` stays referentially stable while dragging — memo skips
// re-rendering (and re-running domainOf/useYouTubeDuration) on every
// pointermove of the track.
export const VideoBody = memo(function VideoBody({
  refItem,
}: {
  refItem: RAGReference;
}) {
  const youtubeId = getYouTubeId(refItem.url);
  const duration = useYouTubeDuration(refItem.url);
  const domain = useMemo(() => domainOf(refItem.url), [refItem.url]);

  return (
    <div className="flex overflow-hidden rounded-[14px] border-[1px] border-[#f0e7da] bg-white">
      <a
        href={refItem.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={refItem.title ? `Play ${refItem.title}` : 'Play video'}
        className="relative block w-[150px] shrink-0 self-stretch overflow-hidden bg-[#2a2d3d]"
      >
        {youtubeId && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={youTubeThumbnailUrl(youtubeId)}
            alt=""
            className="size-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/5 to-black/30" />
        <span className="pointer-events-none absolute left-1/2 top-1/2 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_2px_10px_rgba(0,0,0,0.25)]">
          <Play
            className="ml-0.5 size-4"
            fill="#E36001"
            stroke="#E36001"
            strokeWidth={1}
          />
        </span>
        {duration && (
          <span className="absolute bottom-1.5 right-1.5 rounded-xl bg-black/55 px-1.5 py-0.5 text-[11px] font-semibold text-white">
            {duration}
          </span>
        )}
      </a>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 p-3.5">
        <span
          className="text-[11.5px] font-semibold uppercase text-[#E36001]"
          style={{ letterSpacing: '0.02em' }}
        >
          Video
        </span>
        <h4 className="line-clamp-2 font-[family-name:var(--font-lora)] text-[16.5px] font-semibold leading-snug text-[#31364E]">
          {refItem.title}
        </h4>
        <span className="truncate text-[12.5px] text-[#6b7184]">{domain}</span>
        <a
          href={refItem.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex h-[38px] w-fit items-center gap-1.5 whitespace-nowrap rounded-[22px] bg-[#E36001] px-[16px] text-[13.5px] font-semibold text-white no-underline"
          style={{ boxShadow: V3_SHADOWS.cta }}
        >
          <Play className="size-3.5" fill="#fff" stroke="#fff" />
          Watch
        </a>
      </div>
    </div>
  );
});

// Memoized: same rationale as VideoBody above — SwipeTrack's drag state is
// local to itself, so `refItem` stays referentially stable while dragging.
export const ArticleBody = memo(function ArticleBody({
  refItem,
}: {
  refItem: RAGReference;
}) {
  const domain = useMemo(() => domainOf(refItem.url), [refItem.url]);
  const snippet = refItem.description || refItem.title;
  // Text refs never carry a real thumbnail — same category-aware fallback
  // poster default-preview.ts derives for the RAG answer hero/sources.
  const preview = useMemo(() => defaultPreview(refItem), [refItem]);

  return (
    <div className="flex gap-[16px] rounded-[14px] border-[1px] border-[#f0e7da] bg-white p-[16px]">
      <div className="min-w-0 flex-1">
        <span
          className="block truncate text-[11.5px] font-semibold uppercase text-[#E36001]"
          style={{ letterSpacing: '0.02em' }}
        >
          Article · {domain}
        </span>
        <h4 className="mt-1 line-clamp-2 font-[family-name:var(--font-lora)] text-[16.5px] font-semibold leading-snug text-[#31364E]">
          {refItem.title}
        </h4>
        <div className="mt-1 line-clamp-2 text-[13px] leading-[1.5] text-[#6b7184]">
          {snippet}
        </div>
        <a
          href={refItem.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-block text-[13px] font-semibold text-[#E36001] no-underline"
        >
          Read article →
        </a>
      </div>
      {preview && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={preview.src}
          alt=""
          // Same hide-on-error guard as VideoBody's thumb: a failed poster
          // must never render the browser's broken-image glyph (FB27 #4).
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
          className={cn(
            'size-[92px] shrink-0 rounded-xl',
            preview.fit === 'cover' ? 'object-cover' : 'object-contain'
          )}
          style={
            preview.category === 'wisdom'
              ? {
                  background:
                    'radial-gradient(circle at 50% 30%, #fff3df 0%, #f6d9a6 100%)',
                }
              : undefined
          }
        />
      )}
    </div>
  );
});
